import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { getSemesterActivities, getAllSemesterActivities, createSemesterActivities, deleteSemesterActivity, getCopiableCourses, copyCourseItems } from '../api/evaluationSemesterSeq.api';
import { ISemesterActivity, IGetSemesterActivities, ISemesterActivityReq, ICopiableCourse, IGetCopiableCourses } from '../models/evaluationSemesterSeq.model';

interface ISemesterActivityState {
  semesterActivities: ISemesterActivity[];
  copiableCourses: ICopiableCourse[];
  isLoading: boolean;
  error?: AxiosError | string;
};

const initialState: ISemesterActivityState = {
  semesterActivities: [],
  copiableCourses: [],
  isLoading: false
};

const semesterActivitySlicer = createSlice({
  name: 'semesterActivity',
  initialState,
  reducers: {
    getSemesterActivitiesStart: (state: ISemesterActivityState) => {
      state.semesterActivities = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getSemesterActivitiesSuccess: (state: ISemesterActivityState, action: PayloadAction<IGetSemesterActivities>) => {
      state.semesterActivities = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getSemesterActivitiesFailure: (state: ISemesterActivityState, action: PayloadAction<AxiosError|string>) => {
      state.semesterActivities = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getCopiableCoursesStart: (state: ISemesterActivityState) => {
      state.copiableCourses = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getCopiableCoursesSuccess: (state: ISemesterActivityState, action: PayloadAction<IGetCopiableCourses>) => {
      state.copiableCourses = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getCopiableCoursesFailure: (state: ISemesterActivityState, action: PayloadAction<AxiosError | string>) => {
      state.copiableCourses = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: ISemesterActivityState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (state: ISemesterActivityState, action: PayloadAction<AxiosError|string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: ISemesterActivityState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    deleteSuccess: (state: ISemesterActivityState) => {
      state.isLoading = false;
      state.error = undefined;
    },
  }
});

export const {
  getSemesterActivitiesStart,
  getSemesterActivitiesSuccess,
  getSemesterActivitiesFailure,
  getCopiableCoursesStart,
  getCopiableCoursesSuccess,
  getCopiableCoursesFailure,
  requestStart,
  requestFailure,
  createSuccess,
  deleteSuccess
} = semesterActivitySlicer.actions;

export default semesterActivitySlicer.reducer;

export const doGetSemesterActivities = (semesterCourseCode: string, timeTableSeq: number): any => async (dispatch: any) => {
  try {
    dispatch(getSemesterActivitiesStart());
    const response = await getSemesterActivities(semesterCourseCode, timeTableSeq);
    dispatch(getSemesterActivitiesSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getSemesterActivitiesFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetAllSemesterActivities = (semesterCourseCode: string): any => async (dispatch: any) => {
  try {
    dispatch(getSemesterActivitiesStart());
    const response = await getAllSemesterActivities(semesterCourseCode);
    dispatch(getSemesterActivitiesSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getSemesterActivitiesFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCreateSemesterActivities = (semesterCourseCode: string, timetableSeq: number, data: ISemesterActivityReq): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await createSemesterActivities(semesterCourseCode, timetableSeq, data);
    dispatch(createSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doDeleteSemesterActivity = (semesterSeqEvaluationCode: number): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await deleteSemesterActivity(semesterSeqEvaluationCode);
    dispatch(deleteSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetCopiableCourses = (semesterCourseCode: string): any => async (dispatch: any) => {
  try {
    dispatch(getCopiableCoursesStart());
    const response = await getCopiableCourses(semesterCourseCode);
    dispatch(getCopiableCoursesSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getSemesterActivitiesFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCopyCourseItems = (semesterCourseCode: string, targetSemesterCourseCode: string): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await copyCourseItems(semesterCourseCode, targetSemesterCourseCode);
    dispatch(createSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};
